import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listInvoices } from '../services/invoicesService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import SearchInput from '../components/ui/SearchInput';
import Pagination from '../components/ui/Pagination';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function InvoicesPage() {
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!ruc) return;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await listInvoices({
          ruc,
          type,
          search,
          pageSize: 10,
          desde: desde || null,
          hasta: hasta || null,
        });
        setItems((prev) => (reset ? result.items : [...prev, ...result.items]));
        setHasMore(!!result.hasMore);
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruc, type, search, desde, hasta]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setItems([]);
      fetchPage({ reset: true });
    }, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [ruc, type, search, desde, hasta]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      key: 'tipo',
      label: 'Tipo',
      render: (r) => <Badge tone="info">{r.tipoLabel}</Badge>,
    },
    {
      key: 'serie',
      label: 'Serie-N°',
      render: (r) => `${r.serie || '—'}-${r.correlativo ?? '—'}`,
    },
    { key: 'fechaEmision', label: 'Fecha', render: (r) => formatDate(r.fechaEmision) },
    {
      key: 'cliente',
      label: 'Cliente',
      render: (r) => r.rznSocial || r.numDoc || '—',
    },
    { key: 'total', label: 'Total', render: (r) => formatMoney(r.total) },
    {
      key: 'estado',
      label: 'Estado',
      render: (r) => (
        <span>
          <Badge tone={r.aceptado ? 'ok' : 'warn'}>{r.aceptado ? 'Aceptado' : 'Pendiente'}</Badge>
          {r.status ? <span className="muted"> {r.status}</span> : null}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (r) => (
        <button
          type="button"
          className="btn btn-mini"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/comprobantes/${r.collection}/${r.id}`);
          }}
        >
          Ver
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Comprobantes</h1>
          <p className="muted">
            Facturas, boletas de venta, NC y ND · SUNAT se emite desde la app, aquí se consulta
          </p>
        </div>
      </header>

      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar serie, cliente…" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="select-inline">
          <option value="all">Todos los tipos</option>
          <option value="factura">Facturas</option>
          <option value="boleta">Boletas de venta</option>
          <option value="NC">Notas de crédito</option>
          <option value="ND">Notas de débito</option>
        </select>
        <label className="checkbox">
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="checkbox">
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
      </div>

      {loading ? (
        <LoadingState label="Cargando comprobantes…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchPage({ reset: true })} />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={items}
            keyField="id"
            onRowClick={(row) => navigate(`/admin/comprobantes/${row.collection}/${row.id}`)}
            empty={
              <EmptyState
                title="No hay comprobantes"
                description="Emite desde la app móvil o ajusta el rango de fechas."
              />
            }
          />
          <Pagination hasMore={hasMore} onMore={() => fetchPage()} loading={loadingMore} shown={items.length} />
        </>
      )}
    </div>
  );
}
